/**
 * Portal carry service
 *
 * The logged-in fund member's carry participations and self-editable profile.
 */

import type {
  FundMemberFund,
  FundMemberPortalStatus,
  FundMemberType,
} from '@/types/fundMember';
import type {
  CarryMemberHolding,
  CarryStructureEntity,
  CarryMemberPortfolio,
  CarryUnitPosition,
  CarryVestingPeriod,
} from '@/types/carryFund';
import type {
  CarryDocumentBase,
  CarryFundDocumentsSummary,
} from '@/types/carryDocument';
import { downloadUrlAsFile } from '@/services/documentService';
import { investorPortalApiClient } from '../../shared/utils/api-client';

export interface CarryDocument extends CarryDocumentBase {
  has_file: boolean;
  requires_my_signature: boolean;
}

export interface CarryMemberFundDocumentsSummary extends CarryFundDocumentsSummary {
  awaiting_my_signature: number;
}

export interface CarryFundDocuments {
  fund_id: string;
  fund_name: string;
  documents: CarryDocument[];
}

export interface CarryProfile {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  member_type: FundMemberType;
  email: string | null;
  phone: string | null;
  portal_status: FundMemberPortalStatus;
  last_login_at: string | null;
}

export interface CarryProfileUpdateRequest {
  phone: string | null;
}

interface DownloadUrlResponse {
  download_url: string;
}

export interface CarryHolding extends CarryVestingPeriod, CarryUnitPosition {
  fund_id: string;
  fund_name: string;
  display_name: string | null;
  total_carry_units: number;
}

export interface CarryHoldingDetail extends CarryHolding {
  coc_number: string | null;
  carried_interest: string;
  member_agreement_id: string | null;
  currency: string;
  unit_value: string;
  value: string;
  vested_value: string;
  unvested_value: string;
}

export interface CarryHoldings {
  as_of: string;
  holdings: CarryHolding[];
}

export interface CarryStructureFundPosition {
  fund_id: string;
  name: string;
  display_name: string | null;
  coc_number: string | null;
  document_count: number;
  signed_document_count: number;
}

export interface CarryStructure {
  fund_member_id: string;
  full_name: string;
  member_type: FundMemberType;
  board: string;
  stak: CarryStructureEntity;
  cooperative: CarryStructureEntity;
  holding: CarryMemberHolding | null;
  funds: CarryStructureFundPosition[];
}

export class CarryService {
  static async getFunds(): Promise<FundMemberFund[]> {
    return investorPortalApiClient.get<FundMemberFund[]>('/portal/carry');
  }

  static async getHoldings(): Promise<CarryHoldings> {
    return investorPortalApiClient.get<CarryHoldings>('/portal/carry/holdings');
  }

  static async getHolding(fundId: string): Promise<CarryHoldingDetail> {
    return investorPortalApiClient.get<CarryHoldingDetail>(
      `/portal/carry/holdings/${fundId}`
    );
  }

  static async getHoldingPortfolio(
    fundId: string
  ): Promise<CarryMemberPortfolio> {
    return investorPortalApiClient.get<CarryMemberPortfolio>(
      `/portal/carry/holdings/${fundId}/portfolio`
    );
  }

  static async getDocuments(): Promise<CarryMemberFundDocumentsSummary[]> {
    return investorPortalApiClient.get<CarryMemberFundDocumentsSummary[]>(
      '/portal/carry/documents'
    );
  }

  static async getStructure(): Promise<CarryStructure> {
    return investorPortalApiClient.get<CarryStructure>(
      '/portal/carry/structure'
    );
  }

  static async getFundDocuments(fundId: string): Promise<CarryFundDocuments> {
    return investorPortalApiClient.get<CarryFundDocuments>(
      `/portal/carry/documents/${fundId}`
    );
  }

  static async downloadDocument(
    fundId: string,
    document: CarryDocument
  ): Promise<void> {
    const { download_url } =
      await investorPortalApiClient.get<DownloadUrlResponse>(
        `/portal/carry/documents/${fundId}/${document.id}/download-url`
      );
    await downloadUrlAsFile(download_url, document.file_name ?? document.title);
  }

  static async updateProfile(
    data: CarryProfileUpdateRequest
  ): Promise<{ id: string }> {
    return investorPortalApiClient.patch<{ id: string }>(
      '/portal/carry/profile',
      data
    );
  }
}
